import { useEffect, useMemo, useState } from 'react';
import { auth } from '@actions/services/firestore/browser/firebase';
import { User } from 'firebase/auth';
import useSuperagents from '../../hooks/superagents/useSuperagents';
import AuthenticatingSpinner from './AuthenticatingSpinner';
import { InitialDataContext } from './context';
import { InitialDataProviderProps, InitialData, InitialDataContextValue } from './types';

/**
 * Provider component that fetches initial app data when user is authenticated
 *
 * This provider will:
 * - Block rendering children until Firebase auth state is resolved
 * - Check if the user is authenticated using Firebase auth
 * - Only fetch initial data when user is authenticated
 * - Provide loading states and error handling
 * - Allow refetching of initial data
 */
const InitialDataProvider: React.FC<InitialDataProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [authLoading, setAuthLoading] = useState(!auth.currentUser);

    // Listen to auth state changes
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(firebaseUser => {
            setUser(firebaseUser);
            setAuthLoading(false);
        });

        return unsubscribe;
    }, []);

    const isAuthenticated = !!user && !authLoading;

    // Only fetch superagents when user is authenticated
    const {
        superagents,
        isLoadingSuperagents,
        error: superagentsError,
        refetch: refetchSuperagents,
    } = useSuperagents({ enabled: isAuthenticated });

    // Combine all initial data
    const initialData: InitialData = useMemo(() => {
        return {
            superagents,
        };
    }, [superagents]);

    // Overall loading state - loading if auth is loading or if authenticated and fetching data
    const isLoadingInitialData = authLoading || (isAuthenticated && isLoadingSuperagents);

    // Error state - only show errors if authenticated (not auth errors)
    const error = isAuthenticated ? superagentsError || null : null;

    // Refetch function to reload all initial data
    const refetchInitialData = () => {
        if (isAuthenticated) {
            refetchSuperagents();
        }
    };

    const contextValue: InitialDataContextValue = {
        data: initialData,
        isLoadingInitialData,
        error,
        refetchInitialData,
    };

    // Block rendering children until auth state is resolved to prevent
    // HTTP requests being made before authentication is ready
    if (authLoading) {
        return <AuthenticatingSpinner />;
    }

    return (
        <InitialDataContext.Provider value={contextValue}>{children}</InitialDataContext.Provider>
    );
};

export { InitialDataProvider };
export default InitialDataProvider;
